import React, { useEffect, useMemo, useState } from 'react';
import CaptionGroup from '@ui/captiongroup/CaptionGroup';
import ButtonTabs from '@ui/tabs/ButtonTabs';
import ChartCard from '@ui/chart/ChartCard';
import DataTable, { DataTableColumn, DataTableRow } from '@ui/chart/DataTable';
import LineChart, { LineChartSeries } from '@ui/chart/LineChart';
import type { ValueFormat } from '@ui/chart/chartFormat';
import { formatMonthRange, MonthRange } from '@ui/selector/MonthRangePicker';
import type { EvolutionGrain, RevenueScope, SegmentationView, SegmentEvolution, VatBasis } from '@app/model/bi/Bi';
import { formatDate, MONTH_LABELS, monthsIn, revenueName, WEEK_MAX_MONTHS } from '../Bi.utilities';
import { CompareItem, CompareMetric, compareLine, firstDayOf, lastDayOf } from './Compare.utilities';
import { useCompareEvolutions } from './CompareQueries';

type Props = {
	items: CompareItem[],
	period: MonthRange,
	view: SegmentationView,
	scope: RevenueScope,
	vat: VatBasis,
	// The row of the table that the chart shows (its key and title); null: the Total of each item.
	row: { key: string, title: string } | null,
	// ROO2-310: the Options switch of the page.
	includeOptions: boolean,
	enabled: boolean,
};

const METRICS: { value: CompareMetric, label: string, format: ValueFormat, decimals: number }[] = [
	{ value: 'roomNights', label: 'Room nights', format: 'number', decimals: 0 },
	{ value: 'revenue', label: 'Revenue', format: 'money', decimals: 0 },
	{ value: 'adr', label: 'ADR', format: 'money', decimals: 0 },
	{ value: 'mix', label: 'Mix %', format: 'percent', decimals: 1 },
];
const GRAINS: { value: EvolutionGrain, label: string }[] = [{ value: 'MONTH', label: 'Month' }, { value: 'WEEK', label: 'Week' }];

// The evolution of a Property comparison (D9): one line for each item, by stay month or week, for the Total or for the row
// that the user clicks in the table. The lines take the series colours in the order of the items, as the columns of the
// table. A click on a name of the legend hides that line (the LineChart default, D21). An item that loads or failed keeps
// its name and colour with no line; the table shows its state.
const CompareChartCard: React.FunctionComponent<Props> = ({ items, period, view, scope, vat, row, includeOptions, enabled }) => {
	const [metric, setMetric] = useState<CompareMetric>('roomNights');
	const [chosenGrain, setGrain] = useState<EvolutionGrain>('MONTH');
	const weekOff = monthsIn(period) > WEEK_MAX_MONTHS;
	useEffect(() => {
		if (weekOff) setGrain('MONTH');
	}, [weekOff]);
	const grain: EvolutionGrain = weekOff ? 'MONTH' : chosenGrain;
	// Mix % needs a row: the part of the room nights of the item in each period.
	const shown: CompareMetric = metric === 'mix' && row === null ? 'roomNights' : metric;
	const chosen = METRICS.find(m => m.value === shown) ?? METRICS[0];
	const evolutions = useCompareEvolutions(items, firstDayOf(period.from), lastDayOf(period.to), grain, view, includeOptions, enabled);

	const chart = useMemo(() => {
		const first = evolutions.find(e => e.data !== undefined)?.data;
		if (first === undefined) return null;
		const periods = first.periods;
		// Each item keeps its series slot (its colour in the table and in the line "Compared"): an item that loads or failed
		// is a line with no point.
		const series: LineChartSeries[] = items.map((item, i) => {
			const data: SegmentEvolution | undefined = evolutions[i]?.data;
			return { id: item.id, label: item.label, role: 'series', values: data === undefined ? periods.map(() => null) : compareLine(data, row?.key ?? null, shown, scope, vat) };
		});
		const columns: DataTableColumn[] = [
			{ id: 'period', label: grain === 'MONTH' ? 'Stay month' : 'Stay week from', sortable: false },
			...series.map(s => ({ id: s.id, label: s.label, format: chosen.format, decimals: chosen.decimals, role: 'series' as const, sortable: false })),
		];
		const rows: DataTableRow[] = periods.map((p, i) => ({
			id: p.key,
			cells: Object.fromEntries([['period', grain === 'MONTH' ? `${MONTH_LABELS[Number(p.from.slice(5, 7)) - 1]} ${p.from.slice(0, 4)}` : formatDate(p.from)],
				...series.map(s => [s.id, s.values[i]])]),
		}));
		return { dates: periods.map(p => p.from), series, columns, rows, empty: series.every(s => s.values.every(v => v === null || v === 0)) };
	}, [items, evolutions, row, shown, scope, vat, grain, chosen]);

	const money = shown === 'revenue' || shown === 'adr';
	const selected = money ? ` (${revenueName(scope, vat).replace(' revenue', '')})` : '';
	const name = row?.title ?? 'Total';
	const label = `${chosen.label}${selected} of ${name} by stay ${grain === 'MONTH' ? 'month' : 'week'}, ${formatMonthRange(period)}`;
	const anyFailed = evolutions.some(e => e.state === 'error');
	const state = chart === null ? (anyFailed && evolutions.every(e => e.state === 'error') ? 'error' : 'loading') : (chart.empty ? 'empty' : 'default');

	const controls = <div className="mb-4 mt-4 grid grid-cols-2 gap-x-2 gap-y-4 sm:flex sm:flex-wrap sm:items-center sm:gap-x-4">
		<CaptionGroup caption="Metric" className="col-span-2 w-full sm:col-span-1 sm:w-auto">
			<div className="w-full min-w-0">
				<ButtonTabs label="Metric of the evolution chart" value={shown} onChange={value => setMetric(value as CompareMetric)}
					options={METRICS.map(m => m.value === 'mix' && row === null ? { value: m.value, label: m.label, disabled: true, note: 'Click a row of the table' } : { value: m.value, label: m.label })} />
			</div>
		</CaptionGroup>
		<CaptionGroup caption="Stay period" className="w-full sm:w-auto">
			<div className="w-full min-w-0">
				<ButtonTabs label="Stay period of the evolution chart" value={grain} onChange={value => setGrain(value as EvolutionGrain)}
					options={GRAINS.map(g => g.value === 'WEEK' && weekOff ? { ...g, disabled: true, note: `Week: up to ${WEEK_MAX_MONTHS} months` } : g)} />
			</div>
		</CaptionGroup>
	</div>;

	return <ChartCard title={`Evolution · ${name}`}
		period={`${formatMonthRange(period)} · ${shown === 'adr' ? 'ADR' : chosen.label.toLowerCase()}${selected} by stay ${grain === 'MONTH' ? 'month' : 'week'} · one line for each column of the table · past ${grain === 'MONTH' ? 'months' : 'weeks'} actual, later on the books`}
		info={{ title: 'Evolution', content: 'One line for each property or group of the comparison, with the figures of its own Segmentation report. The chart shows the Total of each one; a click on a row of the table shows that group. A click on a name in the legend hides that line.' }}
		state={state} emptyLabel="No sold room nights in this period." errorLabel="The evolution did not load." onRetry={() => evolutions.forEach(e => e.refetch())}
		table={chart === null ? undefined : <>{controls}<DataTable columns={chart.columns} rows={chart.rows} ariaLabel={label} framed={false} size="normal" wrapHeaders /></>}>
		{controls}
		{chart !== null && <LineChart dates={chart.dates} series={chart.series} format={chosen.format} decimals={chosen.decimals} ariaLabel={label} axisYear="short"
			emptyLabel="No sold room nights in this period." />}
	</ChartCard>;
};

export default CompareChartCard;
